import { RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { KeyboardShortcutKey } from '@/components/ui/KeyboardShortcutKey';
import { StatRow } from '@/components/ui/StatRow';
import { StatusPopover } from '@/components/ui/StatusPopover';
import type { DaemonStatus } from '@/lib/api';
import { formatDuration, formatRelative } from '@/lib/format-duration';

interface DaemonStatusPopoverProps {
  children: ReactNode;
  data: DaemonStatus | undefined;
  isError: boolean;
  onOpenChange: (open: boolean) => void;
  onRunNow: () => void;
  open: boolean;
  runPending: boolean;
}

const DaemonDot = ({ data }: { data: DaemonStatus }) => {
  if (data.stale) {
    return (
      <span className="relative flex size-1.5">
        <span className="relative inline-flex size-1.5 rounded-full bg-score-low" />
      </span>
    );
  }

  if (data.running) {
    return (
      <span className="relative flex size-1.5">
        <span className="absolute inline-flex size-1.5 animate-ping rounded-full bg-accent opacity-75" />
        <span className="relative inline-flex size-1.5 rounded-full bg-accent" />
      </span>
    );
  }

  return (
    <span className="relative flex size-1.5">
      <span className="relative inline-flex size-1.5 rounded-full bg-score-high" />
    </span>
  );
};

const daemonLabel = (data: DaemonStatus) => {
  if (data.stale) {
    return 'Stale';
  }

  if (data.running) {
    return data.mode === 'weekend' ? 'Weekend · no scans' : 'Running';
  }

  return 'Idle';
};

const formatNextScan = (nextScanAt: string | null) => {
  if (!nextScanAt) {
    return '—';
  }

  const diff = new Date(nextScanAt).getTime() - Date.now();

  return diff <= 0 ? '—' : `in ${formatDuration(diff)}`;
};

const formatSchedule = (schedule: DaemonStatus['schedule']) =>
  !schedule ? '—' : `peak ${schedule.peakMin}m · off ${schedule.offpeakMin}m`;

export const DaemonStatusPopover = ({
  children,
  data,
  isError,
  onOpenChange,
  onRunNow,
  open,
  runPending,
}: DaemonStatusPopoverProps) => (
  <StatusPopover onOpenChange={onOpenChange} open={open} trigger={children}>
    <div className="mb-1">
      <div className="font-medium text-foreground">Daemon</div>
      <div className="text-2xs text-subtle">
        job-goblin process status &amp; schedule
      </div>
    </div>

    <div className="my-2 border-t border-border" />

    {isError ? (
      <div className="text-score-low">Failed to load daemon status</div>
    ) : (
      <div className="flex flex-col gap-1">
        <StatRow
          label="Status"
          value={
            <span className="flex items-center gap-1.5">
              {data ? (
                <DaemonDot data={data} />
              ) : (
                <span className="relative flex size-1.5">
                  <span className="relative inline-flex size-1.5 rounded-full bg-border" />
                </span>
              )}
              <span>{data ? daemonLabel(data) : '—'}</span>
            </span>
          }
        />
        <StatRow label="PID" value={data?.pid ?? '—'} />
        <StatRow
          label="Uptime"
          value={formatDuration(data?.uptimeMs ?? null)}
        />
        <StatRow
          label="Last scan"
          value={formatRelative(data?.lastHeartbeatAt ?? null)}
        />
        <StatRow
          label="Next scan"
          value={formatNextScan(data?.nextScanAt ?? null)}
        />
        <StatRow
          label="Schedule"
          value={formatSchedule(data?.schedule ?? null)}
        />
      </div>
    )}

    <div className="my-2 border-t border-border" />

    <div className="flex items-center justify-between gap-2">
      <span className="text-subtle">Trigger a scan manually</span>
      <button
        aria-label="Run pipeline now"
        className="inline-flex cursor-pointer items-center gap-2 rounded-sm bg-accent-soft px-2 py-1 text-foreground hover:bg-accent/30 disabled:cursor-not-allowed disabled:opacity-50"
        disabled={runPending}
        onClick={onRunNow}
        type="button"
      >
        <div className="flex items-center gap-1">
          <RefreshCw size={12} />
          <span>Run now</span>
        </div>
        <div className="flex items-center gap-0.5">
          <KeyboardShortcutKey>K</KeyboardShortcutKey>
          <KeyboardShortcutKey>R</KeyboardShortcutKey>
        </div>
      </button>
    </div>
  </StatusPopover>
);
